import {
  clickGuidedTourTarget,
  closeOpenGuidedTourMenus,
  getGuidedTourMenuPanelSelector,
  GUIDED_TOUR_STEP_TRIGGER_PREVIEW_MS,
  type GuidedTourDefinition,
  waitForGuidedTourUiToSettle,
} from 'components/ui';
import { FIRM_PRIMARY_NAVIGATION_TEST_IDS } from 'layouts/FirmBaseLayout/PrimaryNavigation/PrimaryNavigation.testIds';

export const WORKSPACE_NAVIGATION_TOUR_ID = 'workspace-navigation';

export const GUIDED_TOUR_TARGETS = {
  companySelector: 'guided-tour-company-selector',
  firmIdentity: 'guided-tour-firm-identity',
  helpMenu: 'guided-tour-help-menu',
  measurementDateSelector: 'guided-tour-measurement-date-selector',
  primaryNavigation: 'guided-tour-primary-navigation',
  secondaryNavigation: 'guided-tour-secondary-navigation',
  secondaryNavigationTabs: 'guided-tour-secondary-navigation-tabs',
  tertiaryNavigation: 'guided-tour-tertiary-navigation',
  utilitiesMenu: 'guided-tour-utilities-menu',
} as const;

const clickTourTarget = async (selector: string) => {
  await clickGuidedTourTarget(selector, { closeOpenMenus: true });
};

const openCompanySelector = async () => {
  await clickTourTarget(`[data-tour="${GUIDED_TOUR_TARGETS.companySelector}"]`);
  const firstCompanyOption = document.querySelector<HTMLElement>(
    `[data-testid^="${FIRM_PRIMARY_NAVIGATION_TEST_IDS.companyOption('')}"]`
  );

  firstCompanyOption?.dispatchEvent(new MouseEvent('mouseenter', { bubbles: true }));
  await waitForGuidedTourUiToSettle();
};

const openMeasurementDateSelector = async () => {
  await clickTourTarget(`[data-tour="${GUIDED_TOUR_TARGETS.measurementDateSelector}"]`);
};

const openUtilitiesMenu = async () => {
  await clickTourTarget(`[data-tour="${GUIDED_TOUR_TARGETS.utilitiesMenu}"]`);
};

export const workspaceNavigationTour: GuidedTourDefinition = {
  description: 'Learn the core workspace controls for firm, company, date, navigation, and actions.',
  id: WORKSPACE_NAVIGATION_TOUR_ID,
  steps: [
    {
      body: 'Move between Intelligence, Valuations, Waterfalls, and Documents while keeping your current workspace context.',
      id: 'workspace-navigation',
      placement: 'bottom',
      target: `[data-tour="${GUIDED_TOUR_TARGETS.primaryNavigation}"]`,
      title: 'Workspace navigation',
    },
    {
      actionTarget: `[data-tour="${GUIDED_TOUR_TARGETS.utilitiesMenu}"]`,
      afterStep: closeOpenGuidedTourMenus,
      beforeStepDelayMs: GUIDED_TOUR_STEP_TRIGGER_PREVIEW_MS,
      beforeStep: openUtilitiesMenu,
      body: 'Switch firms, open account or admin tools, and return to guided tours from your user menu.',
      highlightTargets: [
        `[data-tour="${GUIDED_TOUR_TARGETS.utilitiesMenu}"]`,
        getGuidedTourMenuPanelSelector(FIRM_PRIMARY_NAVIGATION_TEST_IDS.optionsMenu),
      ],
      id: 'user-menu',
      placement: 'left',
      target: getGuidedTourMenuPanelSelector(FIRM_PRIMARY_NAVIGATION_TEST_IDS.optionsMenu),
      title: 'User menu',
    },
    {
      actionTarget: `[data-tour="${GUIDED_TOUR_TARGETS.companySelector}"]`,
      afterStep: closeOpenGuidedTourMenus,
      beforeStepDelayMs: GUIDED_TOUR_STEP_TRIGGER_PREVIEW_MS,
      beforeStep: openCompanySelector,
      body: 'Search, pin, and open companies without leaving the current firm.',
      highlightTargets: [
        `[data-tour="${GUIDED_TOUR_TARGETS.companySelector}"]`,
        getGuidedTourMenuPanelSelector(FIRM_PRIMARY_NAVIGATION_TEST_IDS.companyMenu),
      ],
      id: 'company-selector',
      placement: 'right',
      target: getGuidedTourMenuPanelSelector(FIRM_PRIMARY_NAVIGATION_TEST_IDS.companyMenu),
      title: 'Company selector',
    },
    {
      actionTarget: `[data-tour="${GUIDED_TOUR_TARGETS.measurementDateSelector}"]`,
      afterStep: closeOpenGuidedTourMenus,
      beforeStepDelayMs: GUIDED_TOUR_STEP_TRIGGER_PREVIEW_MS,
      beforeStep: openMeasurementDateSelector,
      body: 'Choose the measurement date for company pages. Most Recent uses the latest available date.',
      highlightTargets: [
        `[data-tour="${GUIDED_TOUR_TARGETS.measurementDateSelector}"]`,
        getGuidedTourMenuPanelSelector(FIRM_PRIMARY_NAVIGATION_TEST_IDS.measurementDateMenu),
      ],
      id: 'measurement-date-selector',
      placement: 'right',
      target: getGuidedTourMenuPanelSelector(FIRM_PRIMARY_NAVIGATION_TEST_IDS.measurementDateMenu),
      title: 'Date context',
    },
    {
      afterStep: closeOpenGuidedTourMenus,
      body: 'The first row shows company context and status. The second row moves between company sections and displays page metrics.',
      highlightTargets: [
        `[data-tour="${GUIDED_TOUR_TARGETS.secondaryNavigation}"]`,
        `[data-tour="${GUIDED_TOUR_TARGETS.secondaryNavigationTabs}"]`,
      ],
      id: 'secondary-navigation',
      placement: 'bottom',
      target: `[data-tour="${GUIDED_TOUR_TARGETS.secondaryNavigationTabs}"]`,
      title: 'Company navigation',
    },
    {
      body: 'Use this row for tabs, search, filters, exports, and page actions.',
      id: 'tertiary-navigation',
      placement: 'bottom',
      target: `[data-tour="${GUIDED_TOUR_TARGETS.tertiaryNavigation}"]`,
      title: 'Page actions',
    },
    {
      actionTarget: `[data-testid="${FIRM_PRIMARY_NAVIGATION_TEST_IDS.productTours}"]`,
      actionTargetSequence: [
        `[data-tour="${GUIDED_TOUR_TARGETS.utilitiesMenu}"]`,
        `[data-testid="${FIRM_PRIMARY_NAVIGATION_TEST_IDS.productTours}"]`,
      ],
      actionTargetSequenceDurationMs: GUIDED_TOUR_STEP_TRIGGER_PREVIEW_MS * 2,
      beforeStepDelayMs: GUIDED_TOUR_STEP_TRIGGER_PREVIEW_MS,
      afterStep: closeOpenGuidedTourMenus,
      beforeStep: openUtilitiesMenu,
      body: 'Open Guided Tours from the user menu. Page tours appear when available.',
      highlightTargets: [
        getGuidedTourMenuPanelSelector(FIRM_PRIMARY_NAVIGATION_TEST_IDS.optionsMenu),
        `[data-testid="${FIRM_PRIMARY_NAVIGATION_TEST_IDS.productTours}"]`,
      ],
      id: 'product-tours',
      placement: 'left',
      target: `[data-testid="${FIRM_PRIMARY_NAVIGATION_TEST_IDS.productTours}"]`,
      title: 'Guided tours',
    },
  ],
  title: 'Workspace Navigation',
};
